/** The shell's bootstrap: who the user is, what they may do, what is enabled (`/api/me`). */
import { queryOptions, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { api, unwrap } from './api/client';
import type { Me } from './api/types';

export const meQuery = queryOptions({
  queryKey: ['me'] as const,
  queryFn: () => unwrap(api.GET('/api/me')),
  staleTime: 5 * 60_000,
});

/** The signed-in user. Only valid below the app route, which loads it before rendering. */
export function useMe(): Me {
  return useSuspenseQuery(meQuery).data;
}

/** The user if loaded (for code that may run outside the app route, e.g. the auth pages). */
export function useMaybeMe(): Me | undefined {
  return useQueryClient().getQueryData(meQuery.queryKey);
}

/** A module is enabled for the company (the server decides; RULES §A-2). */
export function moduleEnabled(me: Me, key: string | undefined): boolean {
  if (!key) return true;
  return me.modules.some((m) => m.key === key && m.enabled);
}

/** The server knows the module at all (it may still be disabled for this company). */
export function moduleKnown(me: Me, key: string | undefined): boolean {
  if (!key) return true;
  return me.modules.some((m) => m.key === key);
}

export function useModuleEnabled(key: string | undefined): boolean {
  return moduleEnabled(useMe(), key);
}
